// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  canDeleteBankDetailsInstitution,
  canSaveBankDetailsInstitution,
} from '@client/banks/utils/bank-details-drawer-actions';
import type { AdvisoryBank } from '@shared/data-access/types/brokerage-types';
import { useMemo } from 'react';

export interface BankDetailsDrawerFooterProps {
  readonly institution?: AdvisoryBank | null;
  readonly onClose: () => void;
  readonly onDelete?: () => void;
  readonly onSave?: () => void;
  readonly isSaveDisabled?: boolean;
  readonly isSaveLoading?: boolean;
}

export const useBankDetailsDrawerFooter = ({
  institution = null,
  onClose,
  onDelete,
  onSave,
  isSaveDisabled = false,
  isSaveLoading = false,
}: BankDetailsDrawerFooterProps) => {
  const showDelete = useMemo(
    () => canDeleteBankDetailsInstitution(institution) && Boolean(onDelete),
    [institution, onDelete],
  );

  const showSave = useMemo(
    () => canSaveBankDetailsInstitution(institution) && Boolean(onSave),
    [institution, onSave],
  );

  const showClose = useMemo(() => !showDelete && !showSave, [showDelete, showSave]);

  return {
    onClose,
    onDelete,
    onSave,
    isSaveDisabled,
    isSaveLoading,
    showDelete,
    showSave,
    showClose,
  };
};
